import { call, put, select, takeLatest } from 'redux-saga/effects';
import { DayOff, Holiday, WorkingTime } from 'src/components';
import { getCategoriesTreeForStudio } from 'src/utils/studio';

import { API } from '../../api';
import {
  createStudios,
  createStudiosFailure,
  createStudiosSuccess,
  getFilteredStudios,
  getFilteredStudiosFailure,
  getFilteredStudiosSuccess,
  getStudioById,
  getStudioByIdFailure,
  getStudioByIdSuccess,
  getStudios,
  getStudiosFailure,
  getStudiosSuccess,
  publishStudio,
  publishStudioFailure,
  publishStudioSuccess,
  removeStudios,
  removeStudiosFailure,
  removeStudiosSuccess,
  updateStudios,
  updateStudiosFailure,
  updateStudiosSuccess,
} from '../reducers/studiosSlice';

import { errorHandler } from './common';

function* getStudiosSaga () {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    const searchParams = yield select(state => state.studios.searchParams);
    // @ts-ignore
    const response = yield call<any>(API.get, '/studios/mine', {
      params: {
        ...searchParams,
        // @todo temporary before infinity-scroll/pagination added
        limit: '500',
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    let studios = response.data.data;

    // @ts-ignore
    const responseCategories = yield call<any>(API.get, '/studios/categories', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const categories = responseCategories.data.data;

    // @ts-ignore
    const responseServices = yield call<any>(API.get, '/studios/services', {
      params: {
        limit: '500',
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const services = responseServices.data.data;

    studios = getCategoriesTreeForStudio(studios, categories, services);

    yield put(getStudiosSuccess(studios));
  } catch (error: any) {
    yield errorHandler({
      error,
      recallCallback: getStudiosSaga,
      *callback () {
        yield put(getStudiosFailure(error));
      },
    });
  }
}
interface getStudioByIdSagaProps {
  payload: {
    studioId: number;
  };
}

function* getStudioByIdSaga ({ payload: { studioId } }: getStudioByIdSagaProps) {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(API.get, `/studios/${studioId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    yield put(getStudioByIdSuccess(response.data.data));
  } catch (error: any) {
    yield errorHandler({
      error,
      recallCallback: getStudioByIdSaga,
      *callback () {
        yield put(getStudioByIdFailure(error));
      },
    });
  }
}

function* getFilteredStudiosSaga (action: any) {
  try {
    const searchParams = action.payload;
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(API.get, '/studios/mine', {
      params: {
        ...searchParams,
        limit: '500',
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    let studios = response.data.data;

    // @ts-ignore
    const responseCategories = yield call<any>(API.get, '/studios/categories', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const categories = responseCategories.data.data;

    // @ts-ignore
    const responseServices = yield call<any>(API.get, '/studios/services', {
      params: {
        limit: '500',
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const services = responseServices.data.data;

    studios = getCategoriesTreeForStudio(studios, categories, services);

    yield put(getFilteredStudiosSuccess(studios));
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield getFilteredStudiosSaga(action);
      },
      *callback () {
        yield put(getFilteredStudiosFailure(error));
      },
    });
  }
}

interface createStudiosSagaProp {
  payload: {
    title: string;
    address: string;
    images: string;
    timezone: string;
    admins?: string[];
    masters?: any[];
    schedule: any;
    onSuccess: (response: any) => void;
    onFailure: (error: any) => void;
  };
}

function* createStudiosSaga ({
  payload: { title, address, images, timezone, admins, masters, schedule, onSuccess, onFailure },
}: createStudiosSagaProp) {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(
      API.post,
      '/studios',
      {
        title,
        address,
        images,
        timezone,
        admins,
        masters,
        schedule,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    yield put(createStudiosSuccess());
    onSuccess(response.data.data);
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield createStudiosSaga({
          payload: {
            title,
            address,
            images,
            timezone,
            admins,
            masters,
            schedule,
            onSuccess,
            onFailure,
          },
        });
      },
      *callback () {
        yield put(createStudiosFailure(error));
        onFailure(error);
      },
    });
  }
}

interface updateStudiosSagaProp {
  payload: {
    title?: string;
    address?: string;
    images?: string[];
    admins?: any[];
    schedule: {
      workingTimes: Array<WorkingTime>;
      holidays: Array<Holiday>;
      daysOff: Array<DayOff>;
    };
    services?: any[];
    masters?: any[];
    currentId: number;
    onSuccess: () => void;
    onFailure: (error: any) => void;
  };
}

function* updateStudiosSaga ({
  payload: { title, address, images, admins, schedule, services, masters, currentId, onSuccess, onFailure },
}: updateStudiosSagaProp) {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    yield call<any>(
      API.patch,
      `/studios/${currentId}`,
      {
        title,
        address,
        images,
        admins,
        masters,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    yield put(updateStudiosSuccess());
    onSuccess();
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield updateStudiosSaga({
          payload: {
            title,
            address,
            images,
            admins,
            schedule,
            services,
            masters,
            currentId,
            onSuccess,
            onFailure,
          },
        });
      },
      *callback () {
        yield put(updateStudiosFailure(error));
        onFailure(error);
      },
    });
  }
}

interface removeStudiosSagaProp {
  payload: {
    currentId: number;
    onSuccess: () => void;
    onFailure: (error: any) => void;
  };
}

function* removeStudiosSaga ({ payload: { currentId, onSuccess, onFailure } }: removeStudiosSagaProp) {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(API.delete, `/studios/${currentId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    yield put(removeStudiosSuccess(response.data));
    onSuccess();
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield removeStudiosSaga({
          payload: { currentId, onSuccess, onFailure },
        });
      },
      *callback () {
        yield put(removeStudiosFailure(error));
        onFailure(error);
      },
    });
  }
}


function* publishStudioSaga (action: any) {
  const {studioId, onSuccess, onFailure} = action.payload

  try {
    console.log("action: ", action)

    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(
      API.post,
      `/studios/${studioId}/publish`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    yield put(publishStudioSuccess());
    onSuccess()
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield publishStudioSaga(action);
      },
      *callback () {
        yield put(publishStudioFailure(error));
        onFailure(error)
      },
    });
  }
}

// Watcher Saga
export function* studiosSaga () {
  // @ts-ignore
  yield takeLatest(getStudios().type, getStudiosSaga);
  //@ts-ignore
  yield takeLatest(getStudioById().type, getStudioByIdSaga);
  // @ts-ignore
  yield takeLatest(getFilteredStudios().type, getFilteredStudiosSaga);
  // @ts-ignore
  yield takeLatest(createStudios().type, createStudiosSaga);
  // @ts-ignore
  yield takeLatest(updateStudios().type, updateStudiosSaga);
  // @ts-ignore
  yield takeLatest(removeStudios().type, removeStudiosSaga);
  // @ts-ignore
  yield takeLatest(publishStudio().type, publishStudioSaga);
}
